import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Clock, FileDown, Landmark } from 'lucide-react';
import { useState } from 'react';
import { Alert, Badge, Button, Card, Empty, errorText, Input, Loading, PageHeader, StatCard, Table, Td, Th, useConfirm, useToast } from '../components/ui';
import { download, get, post } from '../lib/api';
import { useAuth } from '../lib/auth';
import { date, dateTime, mt, prevPeriod, today } from '../lib/format';

export function ExportsPage() {
  const { can, session } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const [period, setPeriod] = useState(prevPeriod());
  const [busy, setBusy] = useState<string | null>(null);
  const preview = useQuery({ queryKey: ['at-preview', period], queryFn: () => get(`/exports/preview?period=${period}`) });
  const history = useQuery({ queryKey: ['at-history'], queryFn: () => get('/exports') });

  async function gen(format: string) {
    setBusy(format);
    try { await download(`/exports/at?period=${period}&format=${format}`); qc.invalidateQueries({ queryKey: ['at-history'] }); toast('Ficheiro gerado e arquivado'); }
    catch (e) { toast(errorText(e), 'error'); } finally { setBusy(null); }
  }
  async function submitted(id: string) {
    const reference = await confirm({ title: 'Marcar como submetido no e-Declaração?', description: 'Registe a referência do comprovativo de submissão, se a tiver. Deixa de receber lembretes deste período.', confirmLabel: 'Marcar submetido', input: { label: 'Referência da submissão', placeholder: 'N.º do comprovativo' } });
    if (typeof reference !== 'string') return;
    try { await post(`/exports/${id}/submitted`, { reference }); qc.invalidateQueries({ queryKey: ['at-history'] }); toast('Marcado como submetido'); }
    catch (e) { toast(errorText(e), 'error'); }
  }

  const rows = (preview.data?.rows ?? []) as Record<string, string>[];
  const tot = (k: string) => rows.reduce((s, r) => s + (r.TipoDocumento === 'NC' ? -1 : 1) * Number(r[k]), 0);
  const overdue = preview.data && preview.data.deadline < today();

  return (
    <>
      <PageHeader eyebrow="Fiscal" title="Comunicação à AT" subtitle="Ficheiro mensal de facturas para o portal e-Declaração (Aviso n.º 40/AT/DGI/2025) e SAF-T (MZ)." />
      {session?.tenant.training_mode && (
        <div className="mb-6"><Alert tone="warn" title="Modo formação">Nenhum documento é comunicado enquanto estiver em modo formação: os ficheiros saem vazios. Active a emissão fiscal em Definições.</Alert></div>
      )}
      <Card title="Período" actions={<Input type="month" className="h-8 w-40" value={period} max={today().slice(0, 7)} onChange={(e) => e.target.value && setPeriod(e.target.value)} aria-label="Período" />}>
        {preview.isLoading ? <Loading /> : (
          <>
            <div className="mb-5 grid gap-4 sm:grid-cols-4">
              <StatCard label="Documentos" value={rows.length} />
              <StatCard label="Base tributável" value={mt(tot('TotalBase'))} />
              <StatCard label="IVA" value={mt(tot('TotalIVA'))} />
              <StatCard label="Prazo de entrega" icon={<Clock />} tone={overdue ? 'warn' : undefined} value={date(preview.data?.deadline)} sub="Último dia útil do mês seguinte" />
            </div>
            {can('exports:export') && (
              <div className="flex flex-wrap gap-2">
                <Button loading={busy === 'csv'} onClick={() => gen('csv')}><FileDown className="size-4" />CSV</Button>
                <Button variant="secondary" loading={busy === 'xlsx'} onClick={() => gen('xlsx')}><FileDown className="size-4" />Excel</Button>
                <Button variant="secondary" loading={busy === 'xml'} onClick={() => gen('xml')}><FileDown className="size-4" />XML</Button>
                <Button variant="secondary" loading={busy === 'saft'} onClick={() => gen('saft')}><FileDown className="size-4" />SAF-T (MZ)</Button>
              </div>
            )}
            {rows.length > 0 && (
              <details className="mt-5">
                <summary className="cursor-pointer text-sm text-muted">Ver os {rows.length} documentos que vão no ficheiro</summary>
                <Table>
                  <thead><tr><Th>Documento</Th><Th>Data</Th><Th>NUIT adquirente</Th><Th>Nome</Th><Th className="text-right">Base</Th><Th className="text-right">IVA</Th><Th className="text-right">Total</Th></tr></thead>
                  <tbody>{rows.map((r) => (
                    <tr key={r.NumeroDocumento}><Td>{r.NumeroDocumento}</Td><Td>{date(r.DataEmissao)}</Td><Td>{r.NUITAdquirente}</Td><Td>{r.NomeAdquirente}</Td>
                      <Td className="num">{mt(r.TotalBase)}</Td><Td className="num">{mt(r.TotalIVA)}</Td><Td className="num">{mt(r.TotalDocumento)}</Td></tr>
                  ))}</tbody>
                </Table>
              </details>
            )}
          </>
        )}
      </Card>

      <Card className="mt-6" title="Histórico de ficheiros">
        {history.isLoading ? <Loading /> : !history.data?.items.length ? <Empty icon={<Landmark className="size-6" />} title="Ainda não gerou ficheiros" /> : (
          <Table>
            <thead><tr><Th>Período</Th><Th>Formato</Th><Th className="text-right">Docs</Th><Th className="text-right">IVA</Th><Th>Gerado</Th><Th>Estado</Th><Th /></tr></thead>
            <tbody>
              {history.data.items.map((r: any) => (
                <tr key={r.id}>
                  <Td>{r.period}</Td><Td className="uppercase">{r.format}</Td><Td className="num">{r.documents_count}</Td><Td className="num">{mt(r.tax_total)}</Td>
                  <Td>{dateTime(r.generated_at)}</Td>
                  <Td>{r.submitted_at ? <Badge tone="green"><CheckCircle2 className="mr-1 size-3" />Submetido {date(r.submitted_at)}</Badge> : <Badge tone="amber">Por submeter</Badge>}</Td>
                  <Td className="text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => download(`/exports/${r.id}/download`)}>Descarregar</Button>
                    {!r.submitted_at && can('exports:export') && <Button size="sm" variant="secondary" onClick={() => submitted(r.id)}>Marcar submetido</Button>}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}
